import React, { useMemo, useState } from 'react'
import { CloseIcon, DocumentIcon } from '../../icons/general'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/shad/ui/dialog'

interface FileItemProps {
  /** The file object to display */
  file: File
  /** The index of the file in the array */
  index: number
  /** Function to call when remove button is clicked */
  onRemove: (index: number) => void
  /** Translation function (optional) */
  t?: (text: string) => string
  title: string;
}

/**
 * FileItem component for displaying a file with thumbnail and remove button
 * Supports images and PDF files with different thumbnail displays and fullscreen viewing
 */
const UploadedFilePreview: React.FC<FileItemProps> = ({
  file,
  index,
  onRemove,
  title,
  t = (text: string) => text,
}) => {
  // Create objectURL with useMemo to avoid creating a new one on every render
  const objectUrl = useMemo(() => URL.createObjectURL(file), [file])

  // State for dialog open/close
  const [dialogOpen, setDialogOpen] = useState(false)

  // Determine if file is a PDF
  const isPdf = file.type === 'application/pdf'


  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center space-x-2">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <div className="w-20 h-20 border rounded-md border-gray-300 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity">
              {isPdf ? (
                // PDF thumbnail with document icon
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <DocumentIcon className="w-10 h-10 text-gray-500" />
                </div>
              ) : (
                // Image thumbnail
                <img
                  src={objectUrl}
                  alt={`Uploaded document ${index + 1}`}
                  className="object-cover w-full h-full rounded-md"
                />
              )}
            </div>
          </DialogTrigger>
          <DialogContent className="max-w-4xl w-full p-1 sm:p-6">
            <DialogHeader className="mb-2">
              <DialogTitle>{title ?? file.name}</DialogTitle>
            </DialogHeader>
            <div className="w-full flex items-center justify-center">
              {isPdf ? (
                // PDF viewer
                <iframe
                  src={`${objectUrl}#view=FitH`}
                  title={file.name}
                  className="w-full h-[70vh] border-0"
                />
              ) : (
                // Image viewer
                <img
                  src={objectUrl}
                  alt={file.name}
                  className="max-h-[80vh] max-w-full object-contain"
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
        <div>
          <div className="text-sm font-medium truncate max-w-xs">{title ?? file.name}</div>
          <button
            className="text-sm text-blue-500 hover:text-blue-600 mt-1"
            onClick={() => setDialogOpen(true)}
          >
            {t('View')}
          </button>
        </div>
      </div>
      <button
        className="flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50"
        onClick={() => onRemove(index)}
      >
        {t('remove')}
        <CloseIcon className="ml-2 text-red-500" />
      </button>
    </div>
  )
}

export default UploadedFilePreview
